import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api, centsToDollars, ManualBookingApprovalRequest } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';

function formatMoney(cents: number | null | undefined) {
  return `$${centsToDollars(Math.max(0, Math.round(cents || 0))).toFixed(2)}`;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return 'Not yet';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatTimeRange(startTime: string, endTime: string) {
  const start = new Date(startTime);
  const end = new Date(endTime);
  return `${start.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })} - ${end.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
}

function getQueueTone(queueStatus: ManualBookingApprovalRequest['queueStatus']) {
  if (queueStatus === 'booked') {
    return { background: 'rgba(22, 163, 74, 0.12)', color: '#166534', border: '1px solid rgba(22, 163, 74, 0.18)' };
  }
  if (queueStatus === 'claimed') {
    return { background: 'rgba(37, 99, 235, 0.12)', color: '#1d4ed8', border: '1px solid rgba(37, 99, 235, 0.18)' };
  }
  if (queueStatus === 'declined') {
    return { background: 'rgba(220, 38, 38, 0.12)', color: '#b91c1c', border: '1px solid rgba(220, 38, 38, 0.18)' };
  }
  return { background: 'rgba(245, 158, 11, 0.12)', color: '#b45309', border: '1px solid rgba(245, 158, 11, 0.18)' };
}

function getQueueLabel(queueStatus: ManualBookingApprovalRequest['queueStatus']) {
  if (queueStatus === 'claimed') return 'In Progress';
  if (queueStatus === 'booked') return 'Booked';
  if (queueStatus === 'declined') return 'Refunded';
  return 'Pending Call';
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: '#64748b',
    }}>
      {children}
    </div>
  );
}

export default function SuperAdminManualBookingRequestsScreen() {
  const { isSuperAdmin } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const [requests, setRequests] = useState<ManualBookingApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getManualBookingApprovalRequests();
      setRequests(response.requests || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load manual booking requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchRequests();
    }
  }, [isSuperAdmin, fetchRequests]);

  const updateRequest = (next: ManualBookingApprovalRequest) => {
    setRequests((current) => current.map((request) => (request.id === next.id ? next : request)));
  };

  const handleClaim = async (request: ManualBookingApprovalRequest) => {
    try {
      setBusyId(request.id);
      const response = await api.claimManualBookingApprovalRequest(request.id);
      updateRequest(response.request);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Unable to claim request', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleComplete = async (request: ManualBookingApprovalRequest) => {
    const exactCapture = request.exactCapture !== false;
    const promoNote = request.promoDiscountCents ? `, less the ${request.promoCode || 'promo'} discount` : '';
    const actualVenueCost = window.prompt(
      exactCapture
        ? `Total the venue charged the company card, including its taxes and fees (in dollars).\n\nThe customer is charged exactly this plus the ${formatMoney(request.serviceFeeFlatCents)} PickleCourts fee${promoNote}, never more than the ${formatMoney(request.paymentAmountCents)} hold.`
        : `Total the venue charged the company card, including its taxes and fees (in dollars).\n\nThis market still captures the full ${formatMoney(request.paymentAmountCents)} hold.`,
      ''
    );
    if (actualVenueCost === null) return; // user cancelled the prompt
    if (!actualVenueCost.trim() || isNaN(Number(actualVenueCost))) {
      await showAlert('Required', 'Enter a valid dollar amount for the venue cost.', 'error');
      return;
    }
    const actualVenueCostCents = Math.max(0, Math.round(Number(actualVenueCost) * 100));

    try {
      setBusyId(request.id);
      const { preview } = await api.previewManualBookingCapture(request.id, { actualVenueCostCents });
      const lines = preview.mode === 'exact'
        ? [
          `Venue cost ${formatMoney(preview.actualVenueCostCents)} + PickleCourts fee ${formatMoney(preview.pickleCourtsFeeCents)}${preview.promoDiscountCents > 0 ? ` - promo ${formatMoney(preview.promoDiscountCents)}` : ''}.`,
          `Customer is charged ${formatMoney(preview.captureAmountCents)}; ${formatMoney(preview.releasedCents)} of the hold is released.`,
          ...(preview.shortfallCents > 0
            ? [`This is ${formatMoney(preview.shortfallCents)} more than the hold. Only the hold is charged; PickleCourts absorbs the difference and ops is alerted.`]
            : []),
        ]
        : [`Customer is charged the full ${formatMoney(preview.captureAmountCents)} hold (this market captures the full quote).`];
      const confirmed = await showConfirm('Charge the customer?', lines.join(' '), {
        confirmLabel: `Charge ${formatMoney(preview.captureAmountCents)}`,
      });
      if (!confirmed) return;
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Unable to preview the charge', 'error');
      return;
    } finally {
      setBusyId(null);
    }

    const venueConfirmationCode = window.prompt('Venue confirmation code (optional):', request.venueConfirmationCode || '') || '';

    try {
      setBusyId(request.id);
      const response = await api.completeManualBookingApprovalRequest(request.id, {
        actualVenueCostCents,
        venueConfirmationCode: venueConfirmationCode.trim() || undefined,
      });
      updateRequest(response.request);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Unable to complete request', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDecline = async (request: ManualBookingApprovalRequest) => {
    const reason = window.prompt('Reason for decline/refund:', 'Venue unavailable');
    if (reason === null) return; // user cancelled the prompt
    const finalReason = reason.trim() || 'Venue unavailable';

    try {
      setBusyId(request.id);
      const response = await api.declineManualBookingApprovalRequest(request.id, { reason: finalReason });
      updateRequest(response.request);
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Unable to decline request', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const metrics = useMemo(() => {
    const pending = requests.filter((request) => request.queueStatus === 'pending').length;
    const claimed = requests.filter((request) => request.queueStatus === 'claimed').length;
    const totalChargedCents = requests.reduce((sum, request) => sum + request.paymentAmountCents, 0);
    const totalFeesCents = requests.reduce((sum, request) => sum + request.totalFeeCents, 0);

    return [
      {
        label: 'Pending Calls',
        value: pending.toString(),
        note: 'Ready for ops outreach',
        accent: 'linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(251, 191, 36, 0.06))',
      },
      {
        label: 'In Progress',
        value: claimed.toString(),
        note: 'Actively owned by staff',
        accent: 'linear-gradient(135deg, rgba(37, 99, 235, 0.18), rgba(59, 130, 246, 0.06))',
      },
      {
        label: 'Held Authorizations',
        value: formatMoney(totalChargedCents),
        note: 'Awaiting capture or release',
        accent: 'linear-gradient(135deg, rgba(15, 118, 110, 0.18), rgba(45, 212, 191, 0.06))',
      },
      {
        label: 'Fee Revenue',
        value: formatMoney(totalFeesCents),
        note: 'Service fees earned',
        accent: 'linear-gradient(135deg, rgba(124, 58, 237, 0.18), rgba(196, 181, 253, 0.08))',
      },
    ];
  }, [requests]);

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="card">
          <h2>Manual Booking Requests</h2>
          <p>Super admin access is required.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div style={{ display: 'grid', gap: 24 }}>
        <section
          className="card"
          style={{
            display: 'grid',
            gap: 22,
            padding: 28,
            border: '1px solid rgba(148, 163, 184, 0.14)',
            background: 'radial-gradient(circle at top left, rgba(37, 99, 235, 0.1), transparent 34%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: '0 28px 80px rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 'auto -120px -140px auto',
              width: 260,
              height: 260,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.16) 0%, rgba(16, 185, 129, 0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'grid', gap: 10, maxWidth: 760 }}>
              <SectionLabel>Manual Approval Desk</SectionLabel>
              <div style={{ display: 'grid', gap: 8 }}>
                <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.02, letterSpacing: '-0.04em', color: '#0f172a' }}>
                  Venue call queue for non-partnered bookings
                </h1>
                <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: '#475569' }}>
                  Customer cards are on hold — not charged yet. Ops claims the request, books with the company card,
                  then enters what the venue charged. The customer pays exactly that plus the PickleCourts fee
                  (never more than the hold), or the hold is released.
                </p>
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={fetchRequests}
              disabled={loading}
              style={{
                minWidth: 120,
                height: 42,
                borderRadius: 999,
                border: '1px solid rgba(148, 163, 184, 0.26)',
                background: '#ffffff',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
              }}
            >
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 14,
            }}
          >
            {metrics.map((metric) => (
              <div
                key={metric.label}
                style={{
                  borderRadius: 20,
                  padding: '18px 18px 16px',
                  background: metric.accent,
                  border: '1px solid rgba(148, 163, 184, 0.14)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b' }}>
                  {metric.label}
                </div>
                <div style={{ marginTop: 10, fontSize: 28, fontWeight: 800, letterSpacing: '-0.04em', color: '#0f172a' }}>
                  {metric.value}
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: '#475569' }}>{metric.note}</div>
              </div>
            ))}
          </div>
        </section>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div
            className="card"
            style={{
              padding: 28,
              display: 'grid',
              gap: 10,
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <SectionLabel>Loading Queue</SectionLabel>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Preparing manual booking requests...</div>
            <div style={{ color: '#64748b' }}>Fetching platform-charged bookings waiting on venue outreach.</div>
          </div>
        ) : requests.length === 0 ? (
          <div
            className="card"
            style={{
              padding: 32,
              display: 'grid',
              gap: 12,
              justifyItems: 'start',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <SectionLabel>Queue Clear</SectionLabel>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.04em', color: '#0f172a' }}>No manual requests are waiting.</div>
            <div style={{ maxWidth: 560, color: '#64748b', lineHeight: 1.7 }}>
              Once a customer pays for a non-partnered venue booking, it will appear here with fee breakdown, contact details, and ops actions.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 18 }}>
            {requests.map((request) => {
              const queueTone = getQueueTone(request.queueStatus);
              const isDone = request.queueStatus === 'booked' || request.queueStatus === 'declined';
              const needsChargedResolution = request.paymentStatus === 'succeeded' && request.queueStatus !== 'booked';

              return (
                <section
                  key={request.id}
                  className="card"
                  style={{
                    padding: 0,
                    overflow: 'hidden',
                    border: '1px solid rgba(148, 163, 184, 0.16)',
                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gap: 18,
                      padding: 24,
                      background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.92) 0%, #ffffff 100%)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ display: 'grid', gap: 10 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ fontSize: 23, fontWeight: 800, letterSpacing: '-0.04em', color: '#0f172a' }}>
                            {request.venueName}
                          </div>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '7px 12px',
                              borderRadius: 999,
                              fontSize: 12,
                              fontWeight: 700,
                              ...queueTone,
                            }}
                          >
                            {getQueueLabel(request.queueStatus)}
                          </span>
                          {needsChargedResolution && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '7px 12px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 800,
                                background: 'rgba(220, 38, 38, 0.12)',
                                color: '#b91c1c',
                                border: '1px solid rgba(220, 38, 38, 0.2)',
                              }}
                            >
                              CHARGED - needs booking or refund
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', color: '#475569', fontSize: 14 }}>
                          <span>{request.courtName}</span>
                          <span style={{ color: '#cbd5e1' }}>•</span>
                          <span>{formatDateTime(request.startTime)}</span>
                          <span style={{ color: '#cbd5e1' }}>•</span>
                          <span>{formatTimeRange(request.startTime, request.endTime)}</span>
                        </div>
                      </div>
                      <div style={{ display: 'grid', gap: 8, justifyItems: 'end' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#64748b' }}>
                          Confirmation
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{request.confirmationCode}</div>
                        {request.venueConfirmationCode && (
                          <div style={{ fontSize: 13, color: '#475569' }}>Venue ref: {request.venueConfirmationCode}</div>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(240px, 1.35fr) minmax(220px, 1fr) minmax(220px, 1fr) minmax(240px, 1.2fr)',
                        gap: 14,
                      }}
                    >
                      <div style={panelStyle}>
                        <SectionLabel>Customer</SectionLabel>
                        <div style={panelValueStyle}>{request.customerName || 'Unknown customer'}</div>
                        <div style={panelMetaStyle}>{request.customerEmail || 'No email provided'}</div>
                        <div style={panelMetaStyle}>{request.customerPhone || 'No phone provided'}</div>
                      </div>

                      <div style={panelStyle}>
                        <SectionLabel>{request.queueStatus === 'booked' ? 'Captured' : 'Hold Summary'}</SectionLabel>
                        {request.queueStatus === 'booked' && request.capturedAmountCents != null ? (
                          <>
                            <div style={{ ...panelValueStyle, color: '#166534' }}>{formatMoney(request.capturedAmountCents)}</div>
                            <div style={panelMetaStyle}>Actual venue cost {formatMoney(request.actualVenueCostCents)}</div>
                            <div style={panelMetaStyle}>PickleCourts service fee {formatMoney(request.serviceFeeFlatCents)}</div>
                            {(request.releasedAmountCents ?? 0) > 0 && (
                              <div style={{ ...panelMetaStyle, color: '#166534' }}>Released to customer {formatMoney(request.releasedAmountCents)}</div>
                            )}
                            {(request.shortfallCents ?? 0) > 0 && (
                              <div style={{ ...panelMetaStyle, color: '#b91c1c' }}>Over hold by {formatMoney(request.shortfallCents)} (absorbed)</div>
                            )}
                          </>
                        ) : (
                          <>
                            <div style={panelValueStyle}>{formatMoney(request.paymentAmountCents)}</div>
                            <div style={panelMetaStyle}>Court price {formatMoney(request.subtotalAmountCents)}</div>
                            {(request.serviceFeePercentCents + (request.salesTaxAmountCents || 0)) > 0 && (
                              <div style={panelMetaStyle}>Est. venue tax & fees {formatMoney(request.serviceFeePercentCents + (request.salesTaxAmountCents || 0))}</div>
                            )}
                            <div style={panelMetaStyle}>PickleCourts service fee {formatMoney(request.serviceFeeFlatCents)}</div>
                            {(request.promoDiscountCents ?? 0) > 0 && (
                              <div style={panelMetaStyle}>Promo {request.promoCode || ''} -{formatMoney(request.promoDiscountCents)}</div>
                            )}
                            <div style={panelMetaStyle}>
                              {request.exactCapture === false
                                ? 'Captures the full hold'
                                : 'Charge = venue cost + fee, up to the hold'}
                            </div>
                          </>
                        )}
                      </div>

                      <div style={panelStyle}>
                        <SectionLabel>Ops Timeline</SectionLabel>
                        <div style={panelMetaStyle}><strong>Requested:</strong> {formatDateTime(request.createdAt)}</div>
                        <div style={panelMetaStyle}><strong>Claimed:</strong> {formatDateTime(request.claimedAt)}</div>
                        <div style={panelMetaStyle}><strong>Called:</strong> {formatDateTime(request.calledAt)}</div>
                      </div>

                      <div style={panelStyle}>
                        <SectionLabel>Payment Rail</SectionLabel>
                        <div style={panelValueStyle}>{request.paymentStatus || 'Unknown'}</div>
                        <div style={panelMetaStyle}>Intent {request.paymentIntentId || 'Not recorded'}</div>
                        <div style={panelMetaStyle}>PickleCourts fee {formatMoney(request.totalFeeCents)}</div>
                        <div style={panelMetaStyle}>
                          Company card cost {request.actualVenueCostCents != null ? formatMoney(request.actualVenueCostCents) : request.internalBookingCostCents != null ? formatMoney(request.internalBookingCostCents) : 'Not logged'}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 14,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        paddingTop: 4,
                      }}
                    >
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <span style={detailChipStyle}>Venue slug: {request.venueSlug}</span>
                        <span style={detailChipStyle}>Booking status: {request.status}</span>
                        <span style={detailChipStyle}>Queue state: {request.queueStatus}</span>
                      </div>

                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handleClaim(request)}
                          disabled={busyId === request.id || isDone}
                          style={secondaryActionStyle}
                        >
                          {busyId === request.id && request.queueStatus !== 'pending' ? 'Working...' : 'Claim'}
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleComplete(request)}
                          disabled={busyId === request.id || isDone}
                          style={primaryActionStyle}
                        >
                          {busyId === request.id && request.queueStatus !== 'declined' ? 'Saving...' : 'Mark Booked'}
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDecline(request)}
                          disabled={busyId === request.id || isDone}
                          style={dangerActionStyle}
                        >
                          {busyId === request.id && request.queueStatus === 'declined' ? 'Refunding...' : 'Decline + Refund'}
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const panelStyle: React.CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: 16,
  borderRadius: 18,
  background: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.9)',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
};

const panelValueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 800,
  letterSpacing: '-0.03em',
  color: '#0f172a',
};

const panelMetaStyle: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: '#475569',
};

const detailChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: 999,
  border: '1px solid rgba(226, 232, 240, 0.9)',
  background: '#f8fafc',
  color: '#475569',
  fontSize: 12,
  fontWeight: 600,
};

const secondaryActionStyle: React.CSSProperties = {
  minWidth: 108,
  borderRadius: 999,
  border: '1px solid rgba(148, 163, 184, 0.24)',
  background: '#ffffff',
};

const primaryActionStyle: React.CSSProperties = {
  minWidth: 136,
  borderRadius: 999,
  boxShadow: '0 16px 30px rgba(37, 99, 235, 0.2)',
};

const dangerActionStyle: React.CSSProperties = {
  minWidth: 150,
  borderRadius: 999,
  boxShadow: '0 16px 30px rgba(220, 38, 38, 0.16)',
};
